/* product.js — main product section behavior.
   Loaded by sections/main-product.liquid with <script src defer type="module">.

   <product-info>    — orchestrates the buy box: size radios -> variant sync
                       (hidden name="id" input, prices, availability, URL,
                       gallery jump), purchase-option radios -> hidden
                       selling_plan input + CTA price, add-on toggle, size
                       chart dialog. Variant data (pre-formatted money strings)
                       comes from the section's [data-product-json] script.
   <product-gallery> — thumbnails <-> main <snap-slider> sync (slider.js owns
                       the scroll-snap mechanics; this element only maps thumb
                       clicks to goTo() and mirrors [data-active] onto thumbs).

   Both elements have display:block (product-info in the section {% style %},
   product-gallery in base.css). AJAX add-to-cart itself is handled by
   <product-form>/<cart-upsell> from cart.js — except when an add-on is
   toggled on with cart type "Full page" (no <cart-drawer>), where this file
   posts the main line and the add-ons as one /cart/add.js request. */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-gallery-main]');
    this.thumbSlider = this.querySelector('.fs-mp-thumbs');
    /* Borrow the main slider's own slide list rather than re-querying the DOM.
       The main strip LOOPS, and slider.js implements that by rotating the
       track's children while keeping its `slides` array in block order — so
       the two orders differ, and every index crossing this boundary (a thumb's
       data-index, indexOfMedia's result, the one handed to main.goTo) is a
       block index. Re-querying would capture the rotation instead, and the
       ordering is not even stable: slider.js ships in <head> and product.js at
       the end of the section, so snap-slider has already upgraded — and
       rotated — by the time this runs.
       The fallback covers a main strip that never upgraded (no JS module
       support, or fewer slides than <snap-slider> wires up), where DOM order
       IS block order. */
    this.slides = Array.isArray(this.main && this.main.slides)
      ? this.main.slides
      : Array.from(this.querySelectorAll('[data-gallery-main] [data-slider-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));

    this.thumbs.forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.index);
        /* Highlight NOW, not when the main image finishes travelling. The
           reference assigns activeSlideNum synchronously inside its click
           handler, so the picked thumb brightens on the same tick as the
           press; here the [data-active] observer below is the only thing that
           repaints it, and slider.js does not tag the resting slide until
           80ms after the last scroll event. */
        this.markThumb(index);
        this.select(index);
        /* The trailing per-size thumbnails on the reference double as a
           variant picker — clicking one changes the selected size, price and
           buy box, not just the picture. */
        const variantId = button.dataset.variantId;
        if (variantId) {
          this.dispatchEvent(
            new CustomEvent('gallery:variant', { bubbles: true, detail: { variantId: Number(variantId) } })
          );
        }
      });
    });

    /* slider.js tags the current snap target with [data-active]; mirror it
       onto the thumb strip. */
    this.observer = new MutationObserver(() => {
      this.syncThumbs();
      this.pauseInactive();
    });
    this.slides.forEach((slide) => {
      this.observer.observe(slide, { attributes: true, attributeFilter: ['data-active'] });
    });
    this.syncThumbs();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  goTo(index, behavior) {
    if (this.main && typeof this.main.goTo === 'function') {
      this.main.goTo(index, behavior);
    } else if (this.slides[index]) {
      /* Pre-upgrade fallback: native snap scrolling. */
      this.slides[index].scrollIntoView({ block: 'nearest', inline: 'start' });
    }
  }

  /* An EXPLICIT selection — a thumb click or a variant change. Both strips move
     together and with the same behaviour, which is what the reference does
     (one slideTo per strip inside the same handler). Passive scrolling of the
     main image deliberately does NOT come through here: see syncThumbs. */
  select(index, behavior) {
    this.goTo(index, behavior);
    this.alignThumb(index, behavior);
  }

  /* Pull the picked thumb to the strip's START edge, the way the reference's
     slideTo does — it re-aligns even when the thumb was already fully in view.
     Routed through the thumb slider's own goTo so the destination is a real
     snap offset: the track is `scroll-snap-type: x mandatory`, which retargets
     programmatic scrolls too, so the old flush-to-edge delta (revealThumb) was
     silently rounded to the nearest slide start and could leave the "revealed"
     thumb still clipped. */
  alignThumb(index, behavior) {
    if (this.thumbSlider && typeof this.thumbSlider.goTo === 'function') {
      this.thumbSlider.goTo(index, behavior);
      return;
    }
    const button = this.thumbs[index];
    if (button) this.revealThumb(button, behavior);
  }

  /* Synchronous highlight, decoupled from where the main strip has got to. */
  markThumb(index) {
    this.thumbs.forEach((button, i) => {
      button.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  indexOfMedia(mediaId) {
    if (mediaId === null || mediaId === undefined || !this.slides) return -1;
    return this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
  }

  jumpToMedia(mediaId, behavior) {
    /* <product-gallery> is a descendant of <product-info>: on a theme-editor
       shopify:section:load the ancestor upgrades first, so this can be called
       before connectedCallback has collected the slides. */
    const index = this.indexOfMedia(mediaId);
    if (index < 0) return;
    /* A variant change is an explicit selection, so the thumb strip travels
       with the main image and on the SAME behaviour. Before this the strip was
       moved ~80ms later by the [data-active] observer, which hard-coded
       'smooth' — so a ?variant= deep link painted the right main image at once
       and then visibly slid the thumbnail row sideways a beat afterwards. */
    this.markThumb(index);
    this.select(index, behavior);
  }

  /* A slide that has scrolled away from the snap point must stop making
     noise. Kept out of syncThumbs: that method bails when there are no
     thumbnails, while [data-active] moves either way. */
  pauseInactive() {
    this.slides.forEach((slide) => {
      if (slide.hasAttribute('data-active')) return;
      slide.querySelectorAll('video').forEach((video) => {
        if (!video.paused) video.pause();
      });
    });
  }

  /* Runs from the [data-active] observer, i.e. after ANY rest of the main
     strip — including the shopper's own drag or swipe.

     It keeps aria-current truthful (a thumb marked current while a different
     image is on screen is wrong information for a screen reader, and the
     reference's staleness there is not worth copying), but deliberately does
     NOT move the strip: the reference registers no Swiper callbacks at all
     (`grep '\.on('` over its page source finds none), so dragging the main
     image never scrolls its thumbnails. With mouse drag now on by default,
     keeping the old reveal here meant the thumb row animated on its own
     underneath the shopper's drag. Strip movement belongs to select(). */
  syncThumbs() {
    if (!this.thumbs.length) return;
    let active = this.slides.findIndex((slide) => slide.hasAttribute('data-active'));
    if (active < 0) active = 0;
    this.markThumb(active);
  }

  /* Keep the active thumb visible by scrolling ONLY the thumb strip —
     scrollIntoView can scroll the page itself when the strip is off-viewport.
     Rect-based delta + scrollBy is RTL-safe (same approach as slider.js). */
  revealThumb(button, behavior) {
    const track = button.closest('[data-slider-track]');
    if (!track) return;
    const trackRect = track.getBoundingClientRect();
    const rect = button.getBoundingClientRect();
    let delta = 0;
    if (rect.left < trackRect.left) delta = rect.left - trackRect.left;
    else if (rect.right > trackRect.right) delta = rect.right - trackRect.right;
    if (!delta) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: delta, behavior: reduce ? 'auto' : behavior || 'smooth' });
  }
}
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

class ProductInfo extends HTMLElement {
  connectedCallback() {
    /* The hidden name="id" input ships `disabled` so that, with JS unavailable,
       the section's <noscript> variant <select> is the ONLY `id` the form
       submits. JS is running, so claim it back before anything else can bail
       out — an enabled input plus a stale value is still better than a form
       that POSTs no variant at all. */
    this.idInput = this.querySelector('[data-variant-input]');
    if (this.idInput) this.idInput.disabled = false;

    const dataEl = this.querySelector('[data-product-json]');
    if (!dataEl) return;
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.error('main-product: bad variant data', error);
      return;
    }
    this.variants = this.data.variants || [];
    this.currentId = this.data.selected;

    this.productForm = this.querySelector('product-form');
    this.planInput = this.querySelector('[data-selling-plan-input]');

    /* The gallery is a descendant, so its bubbling request lands here. */
    this.addEventListener('gallery:variant', (event) => {
      this.onGalleryVariant(event.detail?.variantId);
    });

    /* Delegated change handling: size radios, plan radios, plan dropdowns. */
    this.addEventListener('change', (event) => {
      const target = event.target;
      /* Pass the control the shopper actually touched: with more than one
         option dimension, "the first checked radio" is no longer it. */
      if (target.matches('[data-option-position]')) this.onOptionChange(target);
      if (target.matches('[data-plan-radio]')) this.onPlanChange();
      if (target.matches('[data-plan-select]')) {
        const radio = target.closest('[data-plan-card]')?.querySelector('[data-plan-radio]');
        if (radio) {
          radio.value = target.value;
          radio.checked = true;
          /* The picked frequency can be one this variant has no allocation
             for — re-run the guard before anything writes it to the form. */
          this.syncPlanAvailability(this.currentVariant());
          this.onPlanChange();
          this.refreshPlanPrices(this.currentVariant());
        }
      }
    });

    /* Add-ons: when toggled on, add them only after the MAIN add-to-cart
       has succeeded. cart.js dispatches 'cart:open-drawer' exclusively in
       the success path of the <product-form> submit, so that is the success
       signal for this same interaction. The watcher disarms itself on a
       timeout (the failure path fires no event) and on resubmit, so a
       failed add can never leave a dangling listener that fires on an
       unrelated later cart update. Every checked toggle is honored (the
       schema allows two add-on blocks).

       With cart type "page" there is no <cart-drawer>, cart.js lets the native
       POST through and no 'cart:open-drawer' ever fires — so that case gets its
       own single-request path below instead of a listener that dies with the
       page. */
    const form = this.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('submit', (event) => {
        if (this.disarmAddonWatch) this.disarmAddonWatch();
        this.clearAddonError();
        /* Keep toggle + trigger together instead of collecting triggers alone:
           only the picks captured HERE may be reset when the add succeeds. A
           toggle switched on while the request is still in flight was never
           part of this submit, so unchecking it would silently drop a choice
           that was never added. */
        const picked = Array.from(this.querySelectorAll('[data-addon-toggle]'))
          .filter((toggle) => toggle.checked)
          .map((toggle) => ({
            toggle,
            trigger: toggle.closest('cart-upsell')?.querySelector('[data-upsell-add]'),
          }))
          .filter((pair) => pair.trigger);
        if (!picked.length) return;
        if (!document.querySelector('cart-drawer')) {
          this.addWithAddons(
            event,
            form,
            picked.map((pair) => pair.trigger)
          );
          return;
        }
        const onSuccess = () => {
          disarm();
          /* A failed attempt leaves cart.js's [data-form-error] note in the
             form — this drawer-open then came from elsewhere; do nothing. */
          if (form.querySelector('[data-form-error]')) return;
          /* The drawer covers the PDP, so a toggle left ON is invisible — the
             next Add to cart would silently add the add-on a second time. */
          picked.forEach((pair) => {
            pair.trigger.click();
            pair.toggle.checked = false;
          });
        };
        const disarm = () => {
          this.disarmAddonWatch = null;
          window.clearTimeout(timer);
          document.removeEventListener('cart:open-drawer', onSuccess);
        };
        /* cart.js's own bound is REQUEST_TIMEOUT (8 s) plus a settleWithin
           cap of REQUEST_TIMEOUT * 2, i.e. up to 24 s before it dispatches
           'cart:open-drawer' — a shorter watcher would drop the add-on on tail
           latency. If it really does expire the add-on was never posted, so
           say so instead of leaving the toggle checked with no explanation. */
        const timer = window.setTimeout(() => {
          disarm();
          picked.forEach((pair) => {
            const note = pair.toggle.closest('cart-upsell')?.querySelector('[data-cart-error]');
            if (!note) return;
            /* Unhide first, then write: a role="alert" change is only
               announced while the node is rendered. */
            note.hidden = false;
            note.textContent = note.dataset.msgFallback || '';
          });
        }, 25000);
        this.disarmAddonWatch = disarm;
        document.addEventListener('cart:open-drawer', onSuccess, { once: true });
      });
    }

    /* An add-on line can fail on its own after the main line succeeded (sold
       out in the meantime, cart limits). cart.js dispatches 'upsell:failed'
       from the <cart-upsell> and puts the reason in the open drawer — which
       covers the PDP. Mirror it into the add-on block so the shopper still has
       the reason once the drawer is closed, and make sure that add-on's toggle
       is off: nothing was added, so it must not look picked. Only add-ons
       inside this buy box reach this listener. */
    this.addEventListener('upsell:failed', (event) => {
      const upsell = event.target.closest && event.target.closest('cart-upsell');
      if (!upsell) return;
      const toggle = upsell.querySelector('[data-addon-toggle]');
      if (toggle) toggle.checked = false;
      const note = upsell.querySelector('[data-cart-error]');
      if (!note) return;
      const detail = event.detail || {};
      /* Unhide first, then write — see cart.js: a role="alert" change is
         only announced while the node is rendered. */
      note.hidden = false;
      note.textContent = detail.message || note.dataset.msgFallback || '';
    });

    /* Size chart dialog. */
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-size-chart-open]')) {
        this.querySelector('[data-size-chart]')?.showModal();
      }
      if (event.target.closest('[data-size-chart-close]')) {
        this.querySelector('[data-size-chart]')?.close();
      }
      const dialog = event.target.closest('dialog[data-size-chart]');
      if (dialog && event.target === dialog) dialog.close();
    });

    /* Landing state: the section may have preselected a plan the loaded
       variant does not sell (?variant=… deep links, a sold-out default).
       refreshPlanPrices runs too — syncPlanAvailability can re-point a card at
       a different frequency, and the amount printed by Liquid belongs to the
       one it started on. */
    this.syncPlanAvailability(this.currentVariant());
    this.onPlanChange(true);
    this.refreshPlanPrices(this.currentVariant());

    /* ?variant= landing: the buy box is server-rendered for the selected
       variant, the gallery is not — bring it to that variant's media.
       Deferred a frame because <product-gallery> is a descendant: in the theme
       editor the ancestor is upgraded first, so calling it inline would hit an
       element that is not a ProductGallery yet. 'auto' because this runs on
       every load — a landing must arrive on the slide, not animate toward it. */
    this.landingFrame = window.requestAnimationFrame(() => {
      this.landingFrame = 0;
      const gallery = this.querySelector('product-gallery');
      if (gallery && typeof gallery.jumpToMedia === 'function') {
        gallery.jumpToMedia(this.currentVariant()?.media, 'auto');
      }
    });
  }

  /* Wipe the previous attempt's add-on error before a new submit — cart.js
     shares this node, so it can also be holding a message from a stand-alone
     upsell click. */
  clearAddonError() {
    this.querySelectorAll('cart-upsell [data-cart-error]').forEach((note) => {
      note.hidden = true;
      note.textContent = '';
    });
  }

  disconnectedCallback() {
    if (this.disarmAddonWatch) this.disarmAddonWatch();
    if (this.landingFrame) window.cancelAnimationFrame(this.landingFrame);
  }

  /* No-drawer ("Full page" cart type) add: the main line and every checked
     add-on travel in ONE POST /cart/add.js, then we land on the cart page —
     the same destination the native form POST would have reached. If the
     request fails we hand the submit back to the browser so the main product
     is never lost. */
  addWithAddons(event, form, triggers) {
    const idInput = form.querySelector('[name="id"]');
    const fallbackId = this.productForm ? this.productForm.dataset.variantId : '';
    const id = Number((idInput && idInput.value) || fallbackId || 0);
    if (!id) return;
    event.preventDefault();
    const themeRoutes = window.themeRoutes || {};
    const addUrl = themeRoutes.cart_add_url || '/cart/add';
    const cartUrl = themeRoutes.cart_url || '/cart';
    const quantityInput = form.querySelector('[data-product-quantity]');
    const line = { id, quantity: Number(quantityInput && quantityInput.value) || 1 };
    const plan = form.querySelector('[data-selling-plan-input]');
    if (plan && plan.value) line.selling_plan = Number(plan.value);
    const items = [line];
    /* An add-on can carry its own quantity_rule.min (B2B catalogs); posting 1
       against a rule of 2 is rejected with a 422. */
    triggers.forEach((trigger) => {
      const addonId = Number(trigger.dataset.upsellAdd);
      if (addonId) items.push({ id: addonId, quantity: Number(trigger.dataset.upsellQty) || 1 });
    });
    const button = form.querySelector('[type="submit"]');
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', '');
    }
    fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        window.location.assign(cartUrl);
      })
      .catch((error) => {
        console.error('main-product: add with add-ons failed', error);
        if (button) {
          button.removeAttribute('aria-busy');
          button.removeAttribute('disabled');
        }
        /* form.submit() bypasses submit handlers, so this cannot re-enter. */
        form.submit();
      });
  }

  /* A thumbnail that belongs to a variant doubles as a variant picker on the
     reference (its trailing per-size thumbs are badged "SELECTED VARIANT
     IMAGE"). setVariant repaints the radios, price, URL and buy box, and calls
     jumpToMedia — which is a no-op here because the gallery is already on that
     slide, so there is no feedback loop back into this handler. */
  onGalleryVariant(variantId) {
    const variant = this.variants.find((candidate) => candidate.id === variantId);
    if (!variant || variant.id === this.currentId) return;
    this.setVariant(variant);
  }

  currentVariant() {
    return this.variants.find((variant) => variant.id === this.currentId);
  }

  selectedPlanId() {
    const checked = this.querySelector('[data-plan-radio]:checked');
    return checked ? checked.value : '';
  }

  /* Every dimension the picker renders (size tiles + one pill row per extra
     option) is a [data-option-position] radio group, so the selection is the
     ordered tuple of their checked values — positions the picker does not
     render keep the current variant's value. A variant matches only when its
     whole `options` array matches that tuple, position by position.

     Not every tuple exists: the shopper can pick a value no variant offers at
     the other options' current values, and the control is already painted as
     selected by then. So the picker must never leave the form on the previous
     variant — it pins the value that was just changed, relaxes the others and
     takes the closest variant (see resolveOption). Only when that value exists
     on no variant at all do the radios go back to the loaded variant. Whatever
     resolves, setVariant repaints every radio, so no control can stay lit for
     a value the form is not holding. */
  onOptionChange(changed) {
    const current = this.currentVariant();
    const options = this.readOptions(current);
    const match = this.variants.find((variant) => this.optionsMatch(variant, options));
    if (match) {
      this.setVariant(match);
      return;
    }
    const fallback =
      changed && changed.matches('[data-option-position]')
        ? this.resolveOption(options, changed)
        : null;
    if (fallback) {
      this.setVariant(fallback);
      return;
    }
    this.syncOptionRadios(current);
    this.announceSelection();
  }

  /* The ordered option tuple the picker currently shows. */
  readOptions(variant) {
    const options = variant ? [...variant.options] : [];
    this.querySelectorAll('[data-option-position]:checked').forEach((input) => {
      options[Number(input.dataset.optionPosition) - 1] = input.value;
    });
    return options;
  }

  optionsMatch(variant, options) {
    return variant.options.every((value, index) => value === options[index]);
  }

  /* No exact match: keep the value the shopper just chose and give up as few
     of the other choices as possible. Availability outranks every other
     option (the previous single-dimension code preferred an available variant
     too), then each other position that still matches counts for one. */
  resolveOption(options, changed) {
    const pinned = Number(changed.dataset.optionPosition) - 1;
    let best = null;
    let bestScore = -1;
    this.variants.forEach((variant) => {
      if (variant.options[pinned] !== changed.value) return;
      let score = variant.available ? 1000 : 0;
      variant.options.forEach((value, index) => {
        if (index !== pinned && value === options[index]) score += 1;
      });
      if (score > bestScore) {
        bestScore = score;
        best = variant;
      }
    });
    return best;
  }

  /* Repaint every option control from the variant the form actually holds. */
  syncOptionRadios(variant) {
    if (!variant) return;
    this.querySelectorAll('[data-option-position]').forEach((input) => {
      const position = Number(input.dataset.optionPosition) - 1;
      input.checked = variant.options[position] === input.value;
    });
  }

  setVariant(variant) {
    this.currentId = variant.id;

    /* Whatever route got us here — exact match, relaxed match, a plan card,
       a deep link — the controls must show THIS variant's values. */
    this.syncOptionRadios(variant);

    /* ALWAYS keep a variant id in the form (reliability §6). */
    if (this.idInput) this.idInput.value = variant.id;
    if (this.productForm) this.productForm.dataset.variantId = variant.id;

    this.setText('[data-price]', variant.price);
    /* The sr-only price label must not claim a sale the variant isn't in. */
    const priceLabel = this.querySelector('[data-price-label]');
    if (priceLabel) {
      priceLabel.textContent = variant.compare
        ? priceLabel.dataset.msgSale
        : priceLabel.dataset.msgRegular;
    }
    this.querySelectorAll('[data-compare-price]').forEach((el) => {
      el.hidden = !variant.compare;
      const amount = el.querySelector('[data-compare-amount]');
      if (amount) amount.textContent = variant.compare || '';
    });
    this.setText('[data-pack-info]', variant.pack || '');

    /* Unit price — only variants the merchant priced per unit carry one. */
    this.querySelectorAll('[data-unit-wrap]').forEach((el) => {
      el.hidden = !variant.unit;
    });
    if (variant.unit) {
      this.setText('[data-unit-price]', variant.unit);
      this.setText('[data-unit-measure]', variant.unit_measure || '');
    }

    /* This PDP has no quantity stepper, so the line always ships the variant's
       minimum quantity rule (1 unless the merchant set one). */
    const quantityInput = this.querySelector('[data-product-quantity]');
    if (quantityInput) quantityInput.value = variant.qty || 1;

    /* The size readout follows the tile dimension specifically — with a second
       dimension rendered, the first checked radio in DOM order may be a pill. */
    const sizeRadio = this.querySelector('[data-size-option]');
    if (sizeRadio) {
      const sizePosition = Number(sizeRadio.dataset.optionPosition) - 1;
      this.setText('[data-current-size]', variant.options[sizePosition] || '');
    }

    /* Selling plans are per variant: drop a selection the new variant does
       not offer BEFORE any price/CTA/hidden-input write happens. */
    this.syncPlanAvailability(variant);
    this.onPlanChange(true);

    this.refreshPlanPrices(variant);
    this.refreshAvailability(variant.available);
    this.updateCta();
    this.announceSelection();

    /* URL reflects the selection so shares/refreshes keep it — carrying the
       shopper's existing query string over (utm/gclid/affiliate and Shopify's
       own preview params); only `variant` is ours to set. */
    const url = this.dataset.productUrl;
    if (url && window.history && window.history.replaceState) {
      const next = new URL(url, window.location.href);
      next.search = window.location.search;
      next.searchParams.set('variant', variant.id);
      window.history.replaceState(
        {},
        '',
        `${next.pathname}${next.search}${window.location.hash}`
      );
    }

    this.querySelector('product-gallery')?.jumpToMedia(variant.media);
    this.dispatchEvent(
      new CustomEvent('variant:change', { bubbles: true, detail: { variant } })
    );
  }

  /* A selling plan only exists for the variants it was allocated to. Submitting
     one the current variant does not sell makes Shopify reject the line (422),
     so every plan card is re-checked against variant.plans on load, on variant
     change and on frequency change.

     What a card stands for is its GROUP, not the frequency currently picked
     inside it: the frequency <select> rewrites radio.value, so radio.value
     answers "which frequency" and never "which frequencies does this card
     offer". Judging availability by radio.value alone disabled a card the
     variant still sells on another frequency — and since the <select> only
     exists while the card is selected, deselecting it took away the one
     control that could have pointed radio.value back at a valid plan, leaving
     a permanently dead card. So the group's own plan ids ride on the card as
     data-plan-ids, and this method:
       - re-points radio.value (and the <select>) at the first frequency this
         variant IS allocated, whenever the current one is not sold;
       - marks the frequencies with no allocation as disabled <option>s, so an
         invalid one cannot be picked in the first place;
       - disables the card only when the variant sells NO frequency of the
         group — which is recoverable, since picking a variant that does sell
         one re-enables it here.
     A selection that just became invalid falls back to the one-time card (the
     caller runs onPlanChange() right after, which rewrites the hidden
     name="selling_plan" input from whatever ends up checked). */
  syncPlanAvailability(variant) {
    const plans = (variant && variant.plans) || [];
    const sells = (id) => plans.some((plan) => String(plan.plan) === String(id));
    let droppedSelection = false;
    this.querySelectorAll('[data-plan-card]').forEach((card) => {
      const radio = card.querySelector('[data-plan-radio]');
      /* The one-time card carries an empty value and is always valid. */
      if (!radio || !radio.value) return;
      const ids = (card.dataset.planIds || radio.value).split(/\s+/).filter(Boolean);
      const allowed = ids.some(sells);
      if (allowed && !sells(radio.value)) {
        radio.value = ids.find(sells);
      }
      const select = card.querySelector('[data-plan-select]');
      if (select) {
        Array.from(select.options).forEach((option) => {
          option.disabled = !sells(option.value);
        });
        if (allowed) select.value = radio.value;
      }
      radio.disabled = !allowed;
      card.classList.toggle('is-unavailable', !allowed);
      if (!allowed && radio.checked) {
        radio.checked = false;
        droppedSelection = true;
      }
    });
    if (!droppedSelection) return;
    /* [data-requires-plan] = product.requires_selling_plan: the section renders
       no one-time card, because a line with no selling_plan is rejected. Fall
       back to the first frequency this variant still sells instead. */
    if (this.hasAttribute('data-requires-plan')) {
      const nextPlan = Array.from(this.querySelectorAll('[data-plan-radio]')).find(
        (radio) => radio.value && !radio.disabled
      );
      if (nextPlan) nextPlan.checked = true;
      return;
    }
    const onetime = Array.from(this.querySelectorAll('[data-plan-radio]')).find(
      (radio) => !radio.value
    );
    if (onetime) onetime.checked = true;
  }

  refreshPlanPrices(variant) {
    if (!variant) return;
    this.querySelectorAll('[data-plan-card]').forEach((card) => {
      const radio = card.querySelector('[data-plan-radio]');
      if (!radio) return;
      if (!radio.value) {
        const once = card.querySelector('[data-onetime-price]');
        if (once) once.textContent = variant.price;
        /* The one-time card is the only place a plain compare-at price can
           show when the Price block is hidden (the shipped templates hide it). */
        const onceCompare = card.querySelector('[data-onetime-compare]');
        if (onceCompare) {
          onceCompare.hidden = !variant.compare;
          /* The amount only — the <s> also holds the sr-only "Regular price"
             label, which must survive every variant change. */
          const onceAmount = onceCompare.querySelector('[data-onetime-compare-amount]');
          if (onceAmount) onceAmount.textContent = variant.compare || '';
        }
        return;
      }
      const allocation = (variant.plans || []).find(
        (plan) => String(plan.plan) === String(radio.value)
      );
      /* No allocation = this card cannot sell this variant at all. Falling
         back to variant.price would print the ONE-TIME amount on a
         subscription card, i.e. advertise a price the card does not offer —
         so the amount is blanked and the reason note takes over. The note is
         wired to the radio with aria-describedby, so the disabled state is
         announced instead of being carried by the dimming alone. */
      const priceEl = card.querySelector('[data-plan-price]');
      if (priceEl) priceEl.textContent = allocation ? allocation.price : '';
      const compareEl = card.querySelector('[data-plan-compare]');
      if (compareEl) {
        const compare = allocation ? allocation.compare : null;
        compareEl.hidden = !compare;
        const compareAmount = compareEl.querySelector('[data-plan-compare-amount]');
        if (compareAmount) compareAmount.textContent = compare || '';
      }
      const noteEl = card.querySelector('[data-plan-note]');
      if (noteEl) noteEl.hidden = Boolean(allocation);
    });
  }

  refreshAvailability(available) {
    this.querySelectorAll('[data-when-available]').forEach((el) => {
      el.hidden = !available;
      if (el.matches('button')) el.disabled = !available;
    });
    this.querySelectorAll('[data-when-soldout]').forEach((el) => {
      el.hidden = available;
    });
  }

  /* CTA price = selling-plan allocation when a plan is selected, else the
     one-time variant price. */
  updateCta() {
    const variant = this.currentVariant();
    if (!variant) return;
    let price = variant.price;
    const planId = this.selectedPlanId();
    if (planId) {
      const allocation = (variant.plans || []).find(
        (plan) => String(plan.plan) === String(planId)
      );
      if (allocation) price = allocation.price;
    }
    this.setText('[data-cta-price]', price);
  }

  onPlanChange(initial = false) {
    const planId = this.selectedPlanId();
    if (this.planInput) this.planInput.value = planId;
    this.querySelectorAll('[data-plan-card]').forEach((card) => {
      const radio = card.querySelector('[data-plan-radio]');
      card.classList.toggle('is-selected', Boolean(radio && radio.checked));
      if (radio && radio.checked) {
        const title = card.querySelector('[data-plan-title]');
        const label = this.querySelector('[data-selected-plan]');
        if (title && label) label.textContent = title.textContent.trim();
      }
    });
    if (!initial) {
      this.updateCta();
      this.announceSelection();
    }
  }

  /* One polite screen-reader announcement per variant/plan sync, written to
     the section's visually-hidden [data-variant-status] live region:
     "<variant> — <price>, <availability>". Word order and punctuation come
     from the localized templates on that element (Arabic needs its own comma),
     with a title-less variant for single-variant products — announcing
     Shopify's internal "Default Title" would be noise. */
  announceSelection() {
    const status = this.querySelector('[data-variant-status]');
    const variant = this.currentVariant();
    if (!status || !variant) return;
    let price = variant.price;
    const planId = this.selectedPlanId();
    if (planId) {
      const allocation = (variant.plans || []).find(
        (plan) => String(plan.plan) === String(planId)
      );
      if (allocation) price = allocation.price;
    }
    const availability = variant.available
      ? status.dataset.msgAvailable
      : status.dataset.msgSoldout;
    const title = variant.title && variant.title !== 'Default Title' ? variant.title : '';
    const template = title ? status.dataset.msgTemplate : status.dataset.msgTemplateShort;
    if (template) {
      status.textContent = template
        .replace('[title]', title)
        .replace('[price]', price)
        .replace('[availability]', availability);
      return;
    }
    status.textContent = title
      ? `${title} — ${price}, ${availability}`
      : `${price}, ${availability}`;
  }

  setText(selector, value) {
    this.querySelectorAll(selector).forEach((el) => {
      el.textContent = value;
    });
  }
}
if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
